import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, Search, X } from 'lucide-react';
import type { Account } from '../types';
import { ColumnVisibilityToggle, type SiteColumnKey } from './ColumnVisibilityToggle';

interface SitesFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  accountId: string;
  onAccountChange: (value: string) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  accounts: Account[];
  isLoadingAccounts: boolean;
  onExport?: () => void;
  onExportExcel?: () => void;
  // Per-column filters
  nameFilter?: string;
  onNameFilterChange?: (value: string) => void;
  addressFilter?: string;
  onAddressFilterChange?: (value: string) => void;
  tagsFilter?: string;
  onTagsFilterChange?: (value: string) => void;
  locationsFilter?: string;
  onLocationsFilterChange?: (value: string) => void;
  // Clear filters
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  // Column visibility
  visibleColumns?: SiteColumnKey[];
  onVisibleColumnsChange?: (columns: SiteColumnKey[]) => void;
}

export function SitesFilters({
  search,
  onSearchChange,
  accountId,
  onAccountChange,
  pageSize,
  onPageSizeChange,
  accounts,
  isLoadingAccounts,
  onExport,
  onExportExcel,
  nameFilter,
  onNameFilterChange,
  addressFilter,
  onAddressFilterChange,
  tagsFilter,
  onTagsFilterChange,
  locationsFilter,
  onLocationsFilterChange,
  hasActiveFilters,
  onClearFilters,
  visibleColumns,
  onVisibleColumnsChange,
}: SitesFiltersProps) {
  const { t } = useTranslation('sites');

  return (
    <div className="flex flex-col gap-3 flex-1">
      {/* Main toolbar row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('filters.searchPlaceholder', 'Search sites...')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`pl-9 ${search ? 'border-blue-500' : ''}`}
          />
        </div>

        {/* Account Filter */}
        <Select value={accountId} onValueChange={onAccountChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('filters.allAccounts', 'All Accounts')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allAccounts', 'All Accounts')}</SelectItem>
            {!isLoadingAccounts &&
              accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        {/* Page Size */}
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>

        {/* Export Dropdown */}
        {(onExport || onExportExcel) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-1" />
                {t('toolbar.export', 'Export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onExport && (
                <DropdownMenuItem onClick={onExport}>
                  {t('toolbar.exportCSV', 'Export CSV')}
                </DropdownMenuItem>
              )}
              {onExportExcel && (
                <DropdownMenuItem onClick={onExportExcel}>
                  {t('toolbar.exportExcel', 'Export Excel')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Column Visibility */}
        {visibleColumns && onVisibleColumnsChange && (
          <ColumnVisibilityToggle
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={onVisibleColumnsChange}
          />
        )}

        {/* Clear Filters */}
        {hasActiveFilters && onClearFilters && (
          <Button variant="ghost" size="sm" onClick={onClearFilters}>
            <X className="h-4 w-4 mr-1" />
            {t('filters.clear', 'Clear')}
          </Button>
        )}
      </div>

      {/* Per-column filter row */}
      {(onNameFilterChange || onAddressFilterChange || onTagsFilterChange || onLocationsFilterChange) && (
        <div className="flex flex-wrap items-center gap-3">
          {onNameFilterChange && (
            <Input
              placeholder={t('filters.filterByName', 'Filter by name...')}
              value={nameFilter || ''}
              onChange={(e) => onNameFilterChange(e.target.value)}
              className={`max-w-[200px] h-8 text-sm ${nameFilter ? 'border-blue-500' : ''}`}
            />
          )}
          {onAddressFilterChange && (
            <Input
              placeholder={t('filters.filterByAddress', 'Filter by address...')}
              value={addressFilter || ''}
              onChange={(e) => onAddressFilterChange(e.target.value)}
              className={`max-w-[200px] h-8 text-sm ${addressFilter ? 'border-blue-500' : ''}`}
            />
          )}
          {onTagsFilterChange && (
            <Input
              placeholder={t('filters.filterByTags', 'Filter by tags...')}
              value={tagsFilter || ''}
              onChange={(e) => onTagsFilterChange(e.target.value)}
              className={`max-w-[200px] h-8 text-sm ${tagsFilter ? 'border-blue-500' : ''}`}
            />
          )}
          {onLocationsFilterChange && (
            <Input
              placeholder={t('filters.filterByLocations', 'Filter by locations...')}
              value={locationsFilter || ''}
              onChange={(e) => onLocationsFilterChange(e.target.value)}
              className={`max-w-[200px] h-8 text-sm ${locationsFilter ? 'border-blue-500' : ''}`}
            />
          )}
        </div>
      )}
    </div>
  );
}
